import { cx } from "../../lib/cx"
import { useInView } from "../../lib/useInView"

/**
 * The marketing illustrations.
 *
 * Drawn rather than screenshotted. A screenshot of a desktop app shrinks to
 * an unreadable grey smear on a phone, and the reader is left squinting at
 * eight-pixel text to work out what the feature is. These show the idea at
 * three or four large elements, so the same picture works at 380px and at
 * 1400px without a second version.
 *
 * Each one plays when it is scrolled to, and holds still for anyone who has
 * asked their system to reduce motion.
 */

/* -------------------------------------------------------------------------- */
/* Shared parts                                                                */
/* -------------------------------------------------------------------------- */

const INK = "#3d4651"
const MUTED = "#8f9dab"
const LINE = "#e3e9ee"

/** Warm, distinct, and none of them the same hue as another. */
const PEOPLE = {
	amara: { initials: "AO", fill: "#ede9fe", ink: "#6d28d9" },
	wei: { initials: "WC", fill: "#cff8fb", ink: "#0e697e" },
	kwame: { initials: "KM", fill: "#fef3c7", ink: "#92400e" },
	priya: { initials: "PN", fill: "#ffe4e6", ink: "#be123c" },
	tomas: { initials: "TN", fill: "#dcfce7", ink: "#166534" }
}

/**
 * Positioning on the outer group, movement on the inner one.
 *
 * A CSS transform replaces the SVG `transform` attribute rather than adding to
 * it, so animating a group that is also placed by one drops it on the origin
 * the moment the keyframes end.
 */
function Move({ x = 0, y = 0, play, animation, delay = 0, origin = "center", children }) {
	return (
		<g transform={`translate(${x} ${y})`}>
			<g
				className={cx(play && animation)}
				style={{
					opacity: play ? undefined : 0,
					animationDelay: `${delay}ms`,
					transformBox: "fill-box",
					transformOrigin: origin
				}}
			>
				{children}
			</g>
		</g>
	)
}

function Avatar({ person, r = 17, x = 0, y = 0, size = 13 }) {
	const who = PEOPLE[person]
	return (
		<g transform={`translate(${x} ${y})`}>
			<circle r={r} fill={who.fill} />
			<text
				textAnchor="middle"
				y={size * 0.36}
				fontSize={size}
				fontWeight="700"
				fill={who.ink}
				fontFamily="system-ui, sans-serif"
			>
				{who.initials}
			</text>
		</g>
	)
}

function Bar({ x, y, w, h = 7, fill = LINE, rx }) {
	return <rect x={x} y={y} width={w} height={h} rx={rx ?? h / 2} fill={fill} />
}

function Label({ x, y, children, size = 13, weight = 600, fill = INK, anchor = "start" }) {
	return (
		<text
			x={x}
			y={y}
			fontSize={size}
			fontWeight={weight}
			fill={fill}
			textAnchor={anchor}
			fontFamily="system-ui, sans-serif"
		>
			{children}
		</text>
	)
}

/** Every illustration sits on the same rounded, tinted stage. */
function Stage({ viewBox, label, from, to, className, children }) {
	const [ref, play] = useInView()
	const id = label.replace(/\W+/g, "-").toLowerCase()

	return (
		<svg
			ref={ref}
			viewBox={viewBox}
			role="img"
			aria-label={label}
			className={cx("w-full", className)}
		>
			<defs>
				<linearGradient id={`${id}-bg`} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0" stopColor={from} />
					<stop offset="1" stopColor={to} />
				</linearGradient>
			</defs>
			<rect
				width="100%"
				height="100%"
				rx="24"
				fill={`url(#${id}-bg)`}
				style={{ transformBox: "view-box" }}
			/>
			{children(play, id)}
		</svg>
	)
}

/* -------------------------------------------------------------------------- */
/* Hero: what the chain turns into                                             */
/* -------------------------------------------------------------------------- */

const ROWS = [
	{
		n: 1,
		title: "Cladding spec, east elevation",
		tag: "#a3eef4",
		tagW: 46,
		person: "wei",
		done: true
	},
	{
		n: 2,
		title: "Scaffold hand-back date",
		tag: "#fde2c8",
		tagW: 58,
		person: "kwame",
		done: false,
		waiting: true
	},
	{
		n: 3,
		title: "Which drainage survey is current",
		tag: "#ddd6fe",
		tagW: 40,
		person: "amara",
		done: true
	},
	{
		n: 4,
		title: "Crane lift over the next site",
		tag: "#fecdd3",
		tagW: 52,
		person: "priya",
		done: false
	}
]

/**
 * What the chain turns into.
 *
 * The rows carry real questions rather than grey placeholder bars: a reader
 * who has never seen the product needs to know what a "question" is here
 * before the rest of the page means anything. Four short titles do that and
 * still hold together at phone width.
 */
export function TrackedList({ className }) {
	return (
		<Stage
			viewBox="0 0 480 400"
			label="A tracked list of four project questions, two answered and two still open, each with somebody's initials against it"
			from="#cffafe"
			to="#d1fae5"
			className={className}
		>
			{(play) => (
				<>
					{/* Counters */}
					<Move x={26} y={30} play={play} animation="animate-rise">
						<rect width="196" height="58" rx="14" fill="#ffffff" />
						<Label x={20} y={30} size={23} weight={700} fill="#bf5c0a">
							5
						</Label>
						<Label x={46} y={30} size={12} weight={600} fill={MUTED}>
							Open
						</Label>
						<Label x={104} y={30} size={23} weight={700} fill="#10916a">
							3
						</Label>
						<Label x={132} y={30} size={12} weight={600} fill={MUTED}>
							Done
						</Label>
						<rect x={20} y={42} width={156} height="6" rx="3" fill="#fdf3e7" />
						<g
							className={cx(play && "animate-sweep")}
							style={{
								transformBox: "fill-box",
								transformOrigin: "left",
								animationDelay: "500ms"
							}}
						>
							<rect x={20} y={42} width={59} height="6" rx="3" fill="#10916a" />
						</g>
					</Move>

					<Move x={238} y={30} play={play} animation="animate-rise" delay={120}>
						<rect width="216" height="58" rx="14" fill="#ffffff" />
						<Avatar person="amara" x={34} y={29} r={16} size={12} />
						<Avatar person="wei" x={64} y={29} r={16} size={12} />
						<Avatar person="kwame" x={94} y={29} r={16} size={12} />
						<Label x={124} y={34} size={12} weight={600} fill={MUTED}>
							+2 on it
						</Label>
					</Move>

					{/* The list */}
					{ROWS.map((row, index) => (
						<Move
							key={row.n}
							x={26}
							y={112 + index * 70}
							play={play}
							animation="animate-slide-in"
							delay={320 + index * 130}
						>
							<rect
								width="428"
								height="58"
								rx="16"
								fill={row.done ? "#e7f6f0" : "#ffffff"}
							/>

							{/* Ticked, or the ring of something still open. */}
							{row.done ? (
								<g
									className={cx(play && "animate-tick-on")}
									style={{
										transformBox: "fill-box",
										transformOrigin: "center",
										animationDelay: `${900 + index * 200}ms`
									}}
								>
									<circle cx={30} cy={29} r={12} fill="#10916a" />
									<path
										d="M24.5 29l4 4 7-8"
										stroke="#ffffff"
										strokeWidth="2.6"
										strokeLinecap="round"
										strokeLinejoin="round"
										fill="none"
									/>
								</g>
							) : (
								<circle
									cx={30}
									cy={29}
									r={11}
									fill="none"
									stroke="#bf5c0a"
									strokeWidth="2.6"
									strokeOpacity="0.6"
								/>
							)}

							<Label
								x={52}
								y={26}
								size={12}
								weight={700}
								fill={row.done ? "#0b6b4e" : "#8f4407"}
							>
								{`#${row.n}`}
							</Label>
							<Label
								x={76}
								y={26}
								size={13}
								weight={600}
								fill={row.done ? "#0b6b4e" : INK}
							>
								{row.title}
							</Label>
							<Bar x={76} y={36} w={row.tagW} h={11} fill={row.tag} rx={5.5} />
							{row.waiting && <Bar x={76 + row.tagW + 8} y={36} w={76} h={11} fill="#fde2c8" />}

							{row.waiting && (
								<circle
									cx={398}
									cy={29}
									r={16}
									fill="#bf5c0a"
									className={cx(play && "animate-ping-soft")}
									style={{
										transformBox: "fill-box",
										transformOrigin: "center",
										animationDelay: "1.6s"
									}}
								/>
							)}
							<Avatar person={row.person} x={398} y={29} r={16} size={12} />
						</Move>
					))}
				</>
			)}
		</Stage>
	)
}

/* -------------------------------------------------------------------------- */
/* 1. Handing a question to somebody                                           */
/* -------------------------------------------------------------------------- */

export function HandoverArt({ className }) {
	return (
		<Stage
			viewBox="0 0 420 300"
			label="A question being handed to a named person, who is emailed and becomes the one it is waiting on"
			from="#fed7aa"
			to="#cffafe"
			className={className}
		>
			{(play) => (
				<>
					<Move x={28} y={54} play={play} animation="animate-rise">
						<rect width="164" height="104" rx="18" fill="#ffffff" />
						<circle cx={28} cy={30} r={11} fill="none" stroke="#bf5c0a" strokeWidth="2.6" />
						<Label x={46} y={35} size={12} weight={700} fill="#8f4407">
							#4
						</Label>
						<Bar x={20} y={54} w={124} h={9} fill="#e3e9ee" />
						<Bar x={20} y={70} w={92} h={9} fill="#e3e9ee" />
						<Bar x={20} y={88} w={54} h={11} fill="#fde2c8" rx={5.5} />
					</Move>

					{/* The handover itself. */}
					<g fill="none" stroke="#0ba4ba" strokeWidth="3" strokeLinecap="round">
						<path
							d="M200 106 C 240 78, 262 78, 292 96"
							pathLength="1"
							strokeDasharray="1"
							className={cx(play && "animate-draw")}
							style={{ strokeDashoffset: play ? undefined : 1, animationDelay: "520ms" }}
						/>
					</g>
					<Move x={292} y={96} play={play} animation="animate-pop" delay={1200}>
						<path d="M-7 -7 L 6 0 L -7 7 Z" fill="#0ba4ba" />
					</Move>

					<circle
						cx={330}
						cy={128}
						r={30}
						fill="#0ba4ba"
						className={cx(play && "animate-ping-soft")}
						style={{
							transformBox: "fill-box",
							transformOrigin: "center",
							animationDelay: "1.4s"
						}}
					/>
					<Move x={330} y={128} play={play} animation="animate-pop" delay={1100}>
						<circle r={30} fill="#ffffff" />
						<Avatar person="amara" r={26} size={19} />
					</Move>

					{/* The email that carries the question. */}
					<Move x={368} y={92} play={play} animation="animate-pop" delay={1500}>
						<g
							className={play ? "animate-float" : undefined}
							style={{ transformBox: "fill-box", transformOrigin: "center" }}
						>
							<circle r={17} fill="#0e697e" />
							<rect x={-9} y={-6} width="18" height="12" rx="2.5" fill="#ffffff" />
							<path
								d="M-9 -5l9 7 9-7"
								stroke="#0e697e"
								strokeWidth="1.8"
								fill="none"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</g>
					</Move>

					<Move x={232} y={196} play={play} animation="animate-rise" delay={1600}>
						<rect width="164" height="40" rx="20" fill="#fdf3e7" />
						<circle cx={20} cy={20} r={6} fill="#bf5c0a" />
						<Label x={36} y={25} size={13} weight={600} fill="#8f4407">
							Waiting on Amara
						</Label>
					</Move>
				</>
			)}
		</Stage>
	)
}

/* -------------------------------------------------------------------------- */
/* 2. An answer that stays answered                                            */
/* -------------------------------------------------------------------------- */

const ENTRIES = [
	{ person: "wei", w: 188, tint: "#ffffff" },
	{ person: "kwame", w: 150, tint: "#ffffff" }
]

export function LockedArt({ className }) {
	return (
		<Stage
			viewBox="0 0 420 320"
			label="Earlier replies in a thread locking shut as a newer one arrives, leaving only the newest editable"
			from="#ddd6fe"
			to="#cffafe"
			className={className}
		>
			{(play) => (
				<>
					{ENTRIES.map((entry, index) => (
						<Move
							key={entry.person}
							x={30}
							y={26 + index * 86}
							play={play}
							animation="animate-rise"
							delay={index * 160}
						>
							<rect width="360" height="72" rx="18" fill={entry.tint} />
							<Avatar person={entry.person} x={36} y={26} r={16} size={12} />
							<Bar x={62} y={20} w={entry.w} h={9} fill="#e3e9ee" />
							<Bar x={62} y={38} w={entry.w - 46} h={9} fill="#eef2f5" />

							{/* The padlock snaps shut once somebody has replied after. */}
							<Move
								x={326}
								y={36}
								play={play}
								animation="animate-lock-in"
								delay={1500 + index * 220}
							>
								<circle r={17} fill="#eef2f5" />
								<rect x={-7} y={-2} width="14" height="11" rx="3" fill={MUTED} />
								<path
									d="M-4 -2v-4a4 4 0 0 1 8 0v4"
									stroke={MUTED}
									strokeWidth="2.2"
									fill="none"
									strokeLinecap="round"
								/>
							</Move>
						</Move>
					))}

					{/* The newest entry: still yours, still editable. */}
					<Move x={30} y={198} play={play} animation="animate-slide-in" delay={1100}>
						<rect
							width="360"
							height="72"
							rx="18"
							fill="#ffffff"
							stroke="#67dde9"
							strokeWidth="2.5"
						/>
						<Avatar person="priya" x={36} y={26} r={16} size={12} />
						<Bar x={62} y={20} w={206} h={9} fill="#cff8fb" />
						<Bar x={62} y={38} w={148} h={9} fill="#e0f7fa" />
						<Move x={326} y={36} play={play} animation="animate-pop" delay={1700}>
							<circle r={17} fill="#0ba4ba" />
							<path
								d="M-5 5l1-4 7-7 3 3-7 7z"
								fill="#ffffff"
								stroke="#ffffff"
								strokeWidth="1.2"
								strokeLinejoin="round"
							/>
						</Move>
					</Move>

					<Move x={30} y={286} play={play} animation="animate-rise" delay={1900}>
						<Label x={0} y={10} size={12} weight={600} fill="#0e697e">
							Only the newest can still be corrected
						</Label>
					</Move>
				</>
			)}
		</Stage>
	)
}

/* -------------------------------------------------------------------------- */
/* 3. Who sees what                                                            */
/* -------------------------------------------------------------------------- */

export function AccessArt({ className }) {
	const items = [0, 1, 2, 3]
	return (
		<Stage
			viewBox="0 0 420 320"
			label="A team connected to every question in a project, and a single guest connected to just one of them"
			from="#a7f3d0"
			to="#cffafe"
			className={className}
		>
			{(play) => (
				<>
					{/* The team reaches everything. */}
					<g fill="none" stroke="#0ba4ba" strokeWidth="2.6" strokeLinecap="round">
						{items.map((index) => (
							<path
								key={index}
								d={`M136 104 C 190 104, 190 ${44 + index * 66}, 244 ${44 + index * 66}`}
								pathLength="1"
								strokeDasharray="1"
								className={cx(play && "animate-draw")}
								style={{
									strokeDashoffset: play ? undefined : 1,
									animationDelay: `${520 + index * 130}ms`
								}}
							/>
						))}
					</g>

					{/* The guest reaches exactly one. */}
					<path
						d="M136 244 C 190 244, 190 176, 244 176"
						pathLength="1"
						strokeDasharray="1"
						fill="none"
						stroke="#bf5c0a"
						strokeWidth="2.6"
						strokeLinecap="round"
						className={cx(play && "animate-draw")}
						style={{ strokeDashoffset: play ? undefined : 1, animationDelay: "1500ms" }}
					/>

					<Move x={22} y={62} play={play} animation="animate-rise">
						<rect width="114" height="84" rx="18" fill="#ffffff" />
						<Avatar person="wei" x={32} y={32} r={15} size={11} />
						<Avatar person="amara" x={57} y={32} r={15} size={11} />
						<Avatar person="tomas" x={82} y={32} r={15} size={11} />
						<Label x={57} y={66} size={12} weight={700} fill="#0e697e" anchor="middle">
							Your team
						</Label>
					</Move>

					<Move x={22} y={208} play={play} animation="animate-rise" delay={1300}>
						<rect width="114" height="72" rx="18" fill="#ffffff" />
						<Avatar person="kwame" x={57} y={30} r={16} size={12} />
						<Label x={57} y={60} size={12} weight={700} fill="#8f4407" anchor="middle">
							One guest
						</Label>
					</Move>

					{items.map((index) => {
						const guest = index === 2
						return (
							<Move
								key={index}
								x={244}
								y={24 + index * 66}
								play={play}
								animation="animate-pop"
								delay={700 + index * 130}
							>
								<rect
									width="152"
									height="40"
									rx="14"
									fill="#ffffff"
									stroke={guest ? "#fdba74" : "transparent"}
									strokeWidth="2.5"
								/>
								<circle cx={22} cy={20} r={8} fill={guest ? "#fde2c8" : "#cff8fb"} />
								<Bar x={40} y={12} w={94 - index * 8} h={8} fill="#e3e9ee" />
								<Bar x={40} y={26} w={56} h={7} fill="#eef2f5" />
							</Move>
						)
					})}
				</>
			)}
		</Stage>
	)
}

/* -------------------------------------------------------------------------- */
/* 4. Progress                                                                 */
/* -------------------------------------------------------------------------- */

const OPEN_LINE = "M0 26 L56 40 L112 34 L168 62 L224 74 L280 96 L336 112"
const DONE_LINE = "M0 118 L56 106 L112 96 L168 78 L224 58 L280 40 L336 20"

export function ProgressArt({ className }) {
	const bars = [34, 52, 44, 68, 80, 96, 118]
	return (
		<Stage
			viewBox="0 0 420 300"
			label="A chart of questions raised against questions answered, with the answered line climbing past the open one"
			from="#fde68a"
			to="#a7f3d0"
			className={className}
		>
			{(play, id) => (
				<>
					<defs>
						<linearGradient id={`${id}-done`} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0" stopColor="#10916a" stopOpacity="0.45" />
							<stop offset="1" stopColor="#10916a" stopOpacity="0" />
						</linearGradient>
					</defs>

					<Move x={26} y={26} play={play} animation="animate-rise">
						<rect width="368" height="212" rx="20" fill="#ffffff" />
					</Move>

					{/* Raised, as a column each week. */}
					<g transform="translate(58 66)">
						{bars.map((h, index) => (
							<g
								key={index}
								className={cx(play && "animate-grow-up")}
								style={{
									transformBox: "fill-box",
									transformOrigin: "bottom",
									animationDelay: `${420 + index * 80}ms`,
									opacity: play ? undefined : 0
								}}
							>
								<rect
									x={index * 47}
									y={138 - h}
									width="22"
									height={h}
									rx="7"
									fill="#f9c88f"
								/>
							</g>
						))}
					</g>

					{/* Answered, climbing past them. */}
					<g transform="translate(58 66)">
						<path
							d={`${DONE_LINE} L336 138 L0 138 Z`}
							fill={`url(#${id}-done)`}
							className={cx(play && "animate-rise")}
							style={{ opacity: play ? undefined : 0, animationDelay: "1100ms" }}
						/>
						<path
							d={DONE_LINE}
							pathLength="1"
							strokeDasharray="1"
							fill="none"
							stroke="#10916a"
							strokeWidth="3.4"
							strokeLinecap="round"
							strokeLinejoin="round"
							className={cx(play && "animate-draw")}
							style={{ strokeDashoffset: play ? undefined : 1, animationDelay: "900ms" }}
						/>
						<path
							d={OPEN_LINE}
							pathLength="1"
							strokeDasharray="1"
							fill="none"
							stroke="#bf5c0a"
							strokeWidth="3.4"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeOpacity="0.55"
							className={cx(play && "animate-draw")}
							style={{ strokeDashoffset: play ? undefined : 1, animationDelay: "1100ms" }}
						/>
						<Move x={336} y={20} play={play} animation="animate-pop" delay={1900}>
							<circle r={8} fill="#10916a" />
							<circle r={3.4} fill="#ffffff" />
						</Move>
					</g>

					<Move x={26} y={254} play={play} animation="animate-rise" delay={2000}>
						<rect width="176" height="34" rx="17" fill="#e7f6f0" />
						<circle cx={20} cy={17} r={5.5} fill="#10916a" />
						<Label x={34} y={22} size={12} weight={600} fill="#0b6b4e">
							Answered, climbing
						</Label>
					</Move>
					<Move x={218} y={254} play={play} animation="animate-rise" delay={2120}>
						<rect width="176" height="34" rx="17" fill="#fdf3e7" />
						<circle cx={20} cy={17} r={5.5} fill="#bf5c0a" />
						<Label x={34} y={22} size={12} weight={600} fill="#8f4407">
							Raised each week
						</Label>
					</Move>
				</>
			)}
		</Stage>
	)
}
